import { useState } from 'react';
import type { TrainerRosterEntry, TrainingGroupResponse } from '@bm/contracts';
import { Button, Field, Input } from './ui';
import { Modal } from './Modal';

const DEFAULT_COLOR = '#94a3b8';

/** Bearbeiten eines Trainers: individuelle Stundensätze je Trainingsgruppe
 * (Beispiel: Tim JTG1 15€/Std, JTG2 20€/Std, O19 25€/Std) und die
 * Kapsel-Farbe (§ Nutzeranfrage) — siehe docs/trainers.md. Sätze:
 * Ersetzt-alle-Semantik (leeres Feld = kein Satz für diese Gruppe, wird beim
 * Speichern entfernt, falls vorher einer bestand). Farbe: `null` = neutrale
 * Standardfarbe, ein `<input type="color">` kann das selbst nicht abbilden
 * (verlangt immer einen Hex-Wert), daher zeigt es dann `DEFAULT_COLOR` an
 * und ein separater "Zurücksetzen"-Link räumt explizit auf `null`. */
export function TrainerEditModal({
  trainer,
  groups,
  onClose,
  onSave,
  saving,
  error,
}: {
  trainer: TrainerRosterEntry;
  groups: TrainingGroupResponse[];
  onClose: () => void;
  onSave: (rates: Array<{ trainingGroupId: string; hourlyRate: number }>, color: string | null) => void;
  saving: boolean;
  error: string | null;
}) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const r of trainer.rates) {
      initial[r.trainingGroupId] = r.hourlyRate.toString().replace('.', ',');
    }
    return initial;
  });
  const [color, setColor] = useState<string | null>(trainer.color);

  function handleSubmit() {
    const rates: Array<{ trainingGroupId: string; hourlyRate: number }> = [];
    for (const g of groups) {
      const raw = (values[g.id] ?? '').trim().replace(',', '.');
      if (raw === '') continue;
      const num = Number(raw);
      if (Number.isFinite(num) && num > 0) {
        rates.push({ trainingGroupId: g.id, hourlyRate: Math.round(num * 100) / 100 });
      }
    }
    onSave(rates, color);
  }

  return (
    <Modal
      title={`Trainer bearbeiten — ${trainer.firstName} ${trainer.lastName}`}
      subtitle="Stundensätze pro Trainingsgruppe und Kapsel-Farbe für diesen Trainer."
      onClose={onClose}
    >
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      <Field label="Kapsel-Farbe">
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={color ?? DEFAULT_COLOR}
            onChange={(e) => setColor(e.target.value)}
            className="h-9 w-14 cursor-pointer rounded border border-slate-300"
            aria-label="Kapsel-Farbe"
          />
          {color != null && (
            <button type="button" className="text-xs text-blue-600 hover:underline" onClick={() => setColor(null)}>
              Zurücksetzen (Standardfarbe)
            </button>
          )}
        </div>
      </Field>

      <p className="mb-1.5 mt-4 text-xs font-medium text-slate-500">Stundensätze je Trainingsgruppe (€/Std)</p>
      {groups.length === 0 ? (
        <p className="text-sm text-slate-500">Noch keine Trainingsgruppen angelegt.</p>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <Field key={g.id} label={g.name}>
              <Input
                type="text"
                inputMode="decimal"
                placeholder="—"
                value={values[g.id] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [g.id]: e.target.value }))}
              />
            </Field>
          ))}
        </div>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>
          Abbrechen
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Speichert …' : 'Speichern'}
        </Button>
      </div>
    </Modal>
  );
}
